import { Autocomplete, Box, Divider, Paper, Stack, TextField, Typography } from "@mui/material";
import { IComplemento } from "../../../../interfaces/IComplemento";
import { useMemo, useState } from "react";
import { IItemProduto } from "../../../../../pedidos/interface/IItemProduto";
import { calcularPrecoComplemento } from "../../../../../pedidos/utils/calcularPrecoComplemento";
import { IITemComplemento } from "../../../../interfaces/IITemComplemento";
import { useSnackbar } from "../../../../../core/contexts/SnackbarContext";
import { useMoeda } from "../../../../../core/hooks/useMoeda";

interface ComplementoPaperInputProps {
    complemento: IComplemento
    definirItensComplemento: (complementoUuid: string, itens: IITemComplemento[]) => void;
}

export const ComplementoPaperInput = ({ complemento, definirItensComplemento }: ComplementoPaperInputProps) => {
    const { real } = useMoeda();
    const snackbar = useSnackbar();

    const handleItensChange = (event: React.SyntheticEvent<Element, Event>, value: IITemComplemento[]) => {
        if (complemento.numeroMaximoItens) {
            if (value.length > complemento.numeroMaximoItens) {
                return snackbar(`Você já selecionou os ${complemento.numeroMaximoItens} itens`, { severity: "error" })
            }
        }

        // Garantir que os itens selecionados sempre comecem com quantidade 1 (ou undefined)
        // A quantidade da ficha técnica é apenas apresentativa e não deve ser transferida para o pedido
        const itensComQuantidadeCorrigida = value.map(item => {
            // Se o item já estava selecionado, manter sua quantidade atual
            const itemJaSelecionado = complemento.itensSelecionados?.find(i => i.uuid === item.uuid);
            if (itemJaSelecionado && itemJaSelecionado.quantidade !== undefined) {
                return {
                    ...item,
                    quantidade: itemJaSelecionado.quantidade
                };
            }
            // Se é um item novo, começar com quantidade 1 (ou undefined)
            return {
                ...item,
                quantidade: 1
            };
        });

        definirItensComplemento(complemento.uuid, itensComQuantidadeCorrigida);
    }

    const total = useMemo(() => {
        return calcularPrecoComplemento(complemento);
    }, [complemento.itensSelecionados, complemento.precificacao])

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    padding: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Stack width={"100%"} flexDirection={"row"} justifyContent={"space-between"}>
                    <Typography gutterBottom variant="h6" component="div">
                        {complemento.descricao}
                    </Typography>
                    <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                        {real(total)}
                    </Typography>
                </Stack>
                {complemento.itens && (
                    <Autocomplete
                        multiple
                        value={complemento.itensSelecionados}
                        noOptionsText="Nenhuma opção encontrada"
                        onChange={handleItensChange}
                        options={complemento.itens?.filter(item => !item.inativadoAs)}
                        getOptionLabel={(item) => {
                            // Para itens selecionados, mostrar quantidade do pedido (não da ficha técnica)
                            const itemSelecionado = complemento.itensSelecionados?.find(i => i.uuid === item.uuid);
                            const quantidadePedido = itemSelecionado?.quantidade;
                            if (quantidadePedido && quantidadePedido > 1) {
                                return `${quantidadePedido}X - ${item.descricao}`;
                            }
                            // Para opções disponíveis, mostrar apenas a descrição (sem quantidade da ficha técnica)
                            return item.descricao;
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Itens"
                                inputRef={input => input && !complemento.itens.length && input.focus()}
                            />
                        )}
                        sx={{ width: '100%', marginTop: "0.5rem" }}
                    />
                )}
            </Box>
            <Divider />
        </>
    );
}